import { useEffect, useRef, useState } from 'react';
import ContentItemsPanel from './ui/ContentItemsPanel';
import useRightPanelIndividualCall from './useRightPanelIndividualCall';
import getUrlParam, { decodeUrlQueryValue } from 'components/Dashboard/utilities/getUrlParam';
import getAppSite from 'utils/getAppSite';
import { createIncludeRepostsOption } from 'components/Dashboard/Analytics/createAnalyticsState';
import { nations } from 'components/Dashboard/StakeHolders/StakeholderPage/nations';
import dayjs from 'dayjs';

// Splits the single "Written questions" sentiment section into one per nation, each
// queried by questionByContactId (+ country for the devolved nations) instead of contactIds.
const writtenQuestionSentimentSections = [
  { name: 'Written questions', type: 'WrittenQuestion' },
  ...nations.map(({ key, country }) => ({
    name: `Written questions (${key})`,
    type: 'CountrySpecificWrittenQuestion',
    country,
  })),
];

const resolveSectionType = (name) => {
  const writtenQuestionSection = writtenQuestionSentimentSections.find((item) => item.name === name);
  return writtenQuestionSection
    ? { type: writtenQuestionSection.type, country: writtenQuestionSection.country }
    : { type: name };
};

const OverallSentimentRightPanel = (props) => {
  const { contentState, analyticsState, setRightPanelState, stakeholderId, afterLoadingResults } = props;
  const { chartDataOptionSelected } = analyticsState;
  const { text, dataKey, noResults, sentiment, stakeholderPageTitle, dateFilters } = contentState;

  const chartOptionName = chartDataOptionSelected;
  const contributionGraph = chartOptionName === 'Contribution type';
  // The "Contribution type" chart already buckets Written questions across nations into one
  // total (see useCallOverallSentiment), so this single-fetch path must merge them the same way.
  const combinedWrittenQuestions = contributionGraph && dataKey === 'Written questions';

  let sections = sentimentSnapshotSectionValues().flatMap((section) =>
    section === 'Written questions' ? writtenQuestionSentimentSections.map((item) => item.name) : [section]
  );

  const searchParameter = getUrlParam('search');
  const sectionsValues = useRef([]);
  const [activeSection, setActiveSection] = useState();
  const [isLoading, setIsLoading] = useState(!noResults);
  const [results, setResults] = useState(null);

  const pageSize = parseInt(import.meta.env.VITE_APP_PAGE_SIZE);
  const isLoadedComponent = useRef(false);

  const { individualApiCall } = useRightPanelIndividualCall({ analyticsState, contentState });

  const fetchCombinedWrittenQuestions = async ({ pageNumber, source, freeSearch }) => {
    let hits = [];
    let highlights = [];
    let totalHits = 0;
    let thereIsMoreContent = false;
    for (const section of writtenQuestionSentimentSections) {
      let sectionResults = await individualApiCall({
        type: section.type,
        country: section.country,
        pageNumber,
        sentiment,
        snapshot: true,
        source,
        freeSearch,
        stakeholderId,
        dateFilters,
      });
      if (sectionResults) {
        hits = hits.concat(sectionResults.hits ?? []);
        highlights = highlights.concat(sectionResults.highlights ?? []);
        totalHits += sectionResults.totalHits ?? 0;
        thereIsMoreContent = thereIsMoreContent || sectionResults.hits?.length === pageSize;
      }
    }
    return { hits, highlights, totalHits, thereIsMoreContent, pageNumber: pageNumber ?? 1 };
  };

  const initialtItems = useRef(null);
  initialtItems.current = async ({ source }) => {
    try {
      if (noResults) {
        setResults({ hits: [] });
        sectionsValues.current = [];
      } else {
        isLoadedComponent.current = false;
        sectionsValues.current = [];
        setIsLoading(true);
        if (contributionGraph) {
          const freeSearch = !!searchParameter ? decodeUrlQueryValue(searchParameter) : null;
          let results = combinedWrittenQuestions
            ? await fetchCombinedWrittenQuestions({ source, freeSearch })
            : await individualApiCall({
                type: dataKey,
                sentiment,
                snapshot: true,
                source,
                freeSearch,
                stakeholderId,
                dateFilters,
              });
          if (results) {
            setActiveSection(dataKey);
            setResults({
              ...results,
              thereIsMoreContent: combinedWrittenQuestions ? results.thereIsMoreContent : results.hits?.length === pageSize,
            });
            setIsLoading(false);
            isLoadedComponent.current = true;
            if (afterLoadingResults) {
              afterLoadingResults();
            }
          }
        } else {
          let values = [];
          for (let i = 0; i < sections.length; i++) {
            let type = sections[i];
            const { type: resolvedType, country } = resolveSectionType(type);
            let results = await individualApiCall({
              type: resolvedType,
              country,
              sentiment,
              snapshot: true,
              freeSearch: !!searchParameter ? decodeUrlQueryValue(searchParameter) : null,
              source,
              stakeholderId,
            });
            if (results) {
              values.push({
                name: type,
                value: results?.totalHits,
                results: { ...results, thereIsMoreContent: results.hits?.length === pageSize },
              });
            }
          }
          if (values.length > 0) {
            let finalValues = values.filter((item) => item.value > 0);
            sectionsValues.current = finalValues;
            setActiveSection(finalValues[0]?.name);
            setResults(finalValues[0]?.results);
            setIsLoading(false);
            isLoadedComponent.current = true;
          }
        }
      }
    } catch (error) {}
  };

  const callContentItems = async ({ pageNumber }) => {
    try {
      const freeSearch = !!searchParameter ? decodeUrlQueryValue(searchParameter) : null;
      let callResults = combinedWrittenQuestions
        ? await fetchCombinedWrittenQuestions({ pageNumber, freeSearch })
        : await individualApiCall({
            ...resolveSectionType(activeSection),
            pageNumber,
            sentiment,
            snapshot: true,
            freeSearch,
          });
      if (callResults) {
        let hits = results.hits;
        let highlights = results.highlights;

        hits = hits.concat(callResults.hits);
        highlights = highlights.concat(callResults.highlights);

        setResults({
          ...callResults,
          hits,
          highlights,
          thereIsMoreContent: combinedWrittenQuestions ? callResults.thereIsMoreContent : callResults.hits?.length === pageSize,
        });
      }
    } catch (error) {}
  };

  useEffect(() => {
    const source = new AbortController();
    initialtItems.current({ source });
    return () => {
      source.abort('Content items panel cancelled by the user');
    };
  }, [contentState]);

  useEffect(() => {
    if (isLoadedComponent.current && sectionsValues?.current?.length > 0) {
      let section = sectionsValues.current.find((item) => item.name === activeSection);
      setIsLoading(true);
      setResults(noResults ? { hits: [] } : section?.results);
      setTimeout(() => setIsLoading(false), 300);
    }
  }, [activeSection]);

  const load_more_results = async () => {
    let { thereIsMoreContent, pageNumber } = results;
    if (thereIsMoreContent) {
      await callContentItems({ pageNumber: pageNumber + 1 });
    }
  };
  const handleScroll = (scrollValues) => {
    const { scrollTop, clientHeight, scrollHeight } = scrollValues;
    const scrolledToBottom = Math.ceil(scrollTop + clientHeight) >= scrollHeight;
    if (scrolledToBottom) {
      load_more_results();
    }
  };

  return (
    <ContentItemsPanel
      handleScroll={handleScroll}
      setRightPanelState={setRightPanelState}
      text={text}
      sectionsValues={sectionsValues.current}
      isLoading={isLoading}
      activeSection={activeSection}
      setActiveSection={setActiveSection}
      results={results}
      overallSentiment={true}
      stakeholderId={stakeholderId}
      stakeholderPageTitle={stakeholderPageTitle}
    />
  );
};

const sentimentSnapshotSectionValues = () => {
  let sections = ['Spoken parliamentary contributions', 'Written questions', 'EDMs', 'Committee contributions'];
  if (getAppSite() === 'usa') {
    sections = ['Spoken parliamentary contributions'];
  }
  if (import.meta.env.VITE_APP_ENABLE_TWITTER === 'true') {
    sections = [...sections, 'Tweets'];
    if (
      createIncludeRepostsOption({
        filter: [
          dayjs()
            .subtract(import.meta.env.VITE_APP_SENTIMENT_SNAPSHOT_NUM_DAYS ?? 90, 'day')
            .startOf('day')
            .format('YYYY-MM-DD'),
        ],
      })
    ) {
      sections = [...sections, 'Retweets'];
    }
  }
  return sections;
};

export { sentimentSnapshotSectionValues, writtenQuestionSentimentSections, resolveSectionType };
export default OverallSentimentRightPanel;
